import { tsr } from './api.js';

/**
 * Web Push, browser side. This is deliberately the ONLY place any of this lives — see
 * `apps/web/public/sw.js`'s header for the service worker half, and PR7's plan section for why
 * there is no settings page, toggle, or button anywhere else in the product for this.
 *
 * THE PERMISSION FLOW, restated because it is easy to "simplify" back into a nag:
 *  - A one-time, dismissible banner (`features/notifications/PushPermissionBanner.tsx`) offers
 *    the prompt exactly once per browser, gated on `hasInteractedWithPushPrompt()` below. Once a
 *    person has clicked either button, it never renders again — a rejected `Notification.
 *    requestPermission()` cannot be re-requested by script anyway (the browser silently resolves
 *    it to `'denied'` without ever showing UI), so re-offering it would be pure nagging.
 *  - A silent, no-UI re-check (`maybeSilentlyResubscribe`) runs on app mount and on
 *    `visibilitychange` firing to visible. If `Notification.permission` reads `'granted'` but
 *    `hasPostedPushSubscription()` is false, it repeats the subscribe-and-POST sequence with
 *    nothing shown — this is what makes flipping the permission on later, from the browser's own
 *    settings after dismissing the banner, complete the subscription with zero further action.
 *    Deliberately mount + visibilitychange only, never a timer: both are cheap, synchronous,
 *    no-network checks in the common case (already subscribed, or permission not granted).
 */

// ---------------------------------------------------------------------------
// localStorage flags
// ---------------------------------------------------------------------------

/** Set once the one-time banner has been interacted with (accepted OR dismissed) — never re-shown after. */
const PROMPT_INTERACTED_KEY = 'hrms.push.prompt-interacted';
/** Set ONLY after a subscription has been successfully POSTed to the server this browser — see
 * `maybeSilentlyResubscribe`'s header for why this, not `pushManager.getSubscription()`, is the
 * source of truth: the task is "has the server heard about this browser", not "does a subscription
 * object exist locally". */
const SUBSCRIBED_KEY = 'hrms.push.subscribed';

/** Wrapped exactly like `token-store.ts`'s `safeLocalStorage`: private-browsing modes and blocked
 * site data throw on access, and a push-prompt banner failing open (never gating) beats a crash. */
function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readFlag(key: string): boolean {
  try {
    return safeLocalStorage()?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    safeLocalStorage()?.setItem(key, '1');
  } catch {
    // Best-effort — see `safeLocalStorage`'s header. Worst case the banner or the silent re-check
    // runs again next time, which is harmless (an idempotent subscribe, or a dismiss re-shown).
  }
}

export function hasInteractedWithPushPrompt(): boolean {
  return readFlag(PROMPT_INTERACTED_KEY);
}

export function markPushPromptInteracted(): void {
  writeFlag(PROMPT_INTERACTED_KEY);
}

export function hasPostedPushSubscription(): boolean {
  return readFlag(SUBSCRIBED_KEY);
}

export function markPushSubscriptionPosted(): void {
  writeFlag(SUBSCRIBED_KEY);
}

// ---------------------------------------------------------------------------
// Pure decision logic — the part worth unit testing without a browser Push API
// ---------------------------------------------------------------------------

/**
 * Whether the one-time prompt should render. `Notification.permission === 'denied'` is excluded
 * deliberately (rule 6 of the task): a denied permission cannot be re-requested by script, so
 * showing a button that silently does nothing would be worse than showing none.
 */
export function shouldShowPushPrompt(
  supported: boolean,
  permission: NotificationPermission,
  interacted: boolean,
): boolean {
  return supported && permission === 'default' && !interacted;
}

/**
 * Whether the silent, no-UI re-check should attempt a subscribe-and-POST. Only ever true when
 * permission is ALREADY granted (this never triggers the native permission dialog) and this
 * browser has no record of a successful POST yet.
 */
export function shouldSilentlyResubscribe(
  supported: boolean,
  permission: NotificationPermission,
  alreadyPosted: boolean,
): boolean {
  return supported && permission === 'granted' && !alreadyPosted;
}

// ---------------------------------------------------------------------------
// Browser feature detection
// ---------------------------------------------------------------------------

/**
 * Whether this build was compiled with a VAPID public key. Without one `subscribeAndRegisterPush`
 * can only skip, so the one-time banner must not offer an "Enable" button that would do nothing and
 * then be marked as answered forever. `VITE_VAPID_PUBLIC_KEY` is baked in at build time.
 */
export function isWebPushConfigured(): boolean {
  return Boolean(import.meta.env.VITE_VAPID_PUBLIC_KEY);
}

/**
 * The browser's notification permission, or 'denied' where the Notification API does not exist at all
 * (iPhone Safari outside a Home Screen app, older browsers, jsdom). Reading `Notification.permission`
 * directly there throws a ReferenceError, and this runs while the app shell mounts — so it must never
 * be read without this guard.
 */
export function currentPushPermission(): NotificationPermission {
  return typeof Notification === 'undefined' ? 'denied' : Notification.permission;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// ---------------------------------------------------------------------------
// VAPID key conversion
// ---------------------------------------------------------------------------

/**
 * `PushManager.subscribe`'s `applicationServerKey` wants a `Uint8Array`, not the base64url string
 * VAPID keys are normally handed around as. This is the well-known conversion (the same few lines
 * every Web Push guide reproduces) — written inline per the task rather than pulled in as a
 * dependency for four lines of math.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ---------------------------------------------------------------------------
// Service worker registration
// ---------------------------------------------------------------------------

/**
 * Called once at app load (see `main.tsx`), unconditionally of auth state or push permission — a
 * service worker has to already be registered and active by the time anything tries to read
 * `navigator.serviceWorker.ready`, and registering it early costs nothing (no fetch handler, no
 * caching — see `public/sw.js`'s header).
 */
export async function registerServiceWorker(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    // Never louder than a console line — same posture as `lib/socket.tsx`'s connection handling.
    // A failed registration just means push never works this session; nothing else in the app
    // depends on it.
    console.warn('[webpush] service worker registration failed', err);
  }
}

// ---------------------------------------------------------------------------
// Subscribe + POST
// ---------------------------------------------------------------------------

/**
 * Assumes `Notification.permission === 'granted'` already — callers (the banner's "Enable" click,
 * and the silent re-check) are responsible for that precondition; this never itself prompts.
 *
 * Never throws: every failure (missing VAPID key, no service worker, `subscribe()` rejecting, the
 * POST failing) resolves `false` and logs a `console.warn`, so a caller can use the result to
 * decide whether to mark the flag without needing a try/catch of its own.
 */
export async function subscribeAndRegisterPush(): Promise<boolean> {
  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    console.warn('[webpush] VITE_VAPID_PUBLIC_KEY is not set; skipping subscribe');
    return false;
  }
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      console.warn('[webpush] subscription missing expected fields', json);
      return false;
    }

    const result = await tsr.notifications.subscribeWebPush.mutate({
      body: {
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        userAgent: navigator.userAgent,
      },
    });
    if (result.status !== 200) {
      console.warn('[webpush] subscribe POST rejected', result.status);
      return false;
    }

    markPushSubscriptionPosted();
    return true;
  } catch (err) {
    console.warn('[webpush] subscribe failed', err);
    return false;
  }
}

/**
 * The silent, no-UI half of the flow (task item 5). Safe to call unconditionally from an effect —
 * `shouldSilentlyResubscribe` is a synchronous, local check that resolves to a no-op in the common
 * case (already subscribed, or permission not granted) with no network call at all.
 */
export async function maybeSilentlyResubscribe(): Promise<void> {
  if (
    !shouldSilentlyResubscribe(
      isPushSupported(),
      currentPushPermission(),
      hasPostedPushSubscription(),
    )
  ) {
    return;
  }
  await subscribeAndRegisterPush();
}
